import { AppModal } from '@euler/app/components/modal/AppModal';
import { Typography } from '@euler/app/components/typography';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { ButtonThemeProps } from '@euler/app/tv/theme/types';
import { MaybeText } from '@euler/components/MaybeText';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useBehaviorSubject,
  useBehaviorSubjectUpdater,
  useCreation,
} from '@euler/utils/hooks';
import { LinearGradient } from 'expo-linear-gradient';
import {
  ReactNode,
  createContext,
  memo,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import {
  ActivityIndicator,
  StyleProp,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { BehaviorSubject } from 'rxjs';

export type AlertButtonProps = {
  text: string;
  style?: StyleProp<ViewStyle>;
  destructive?: boolean;
  type?: 'cancel' | 'confirm' | 'default';
  disabled$?: BehaviorSubject<boolean | undefined>;
  loading$?: BehaviorSubject<boolean | undefined>;
  onPress?: () => void;
};

export type AlertProps = {
  title: string;
  message: string | ReactNode;
  buttons: AlertButtonProps[];
  style?: StyleProp<ViewStyle>;
  titleStyle?: StyleProp<TextStyle>;
  messageStyle?: StyleProp<TextStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  onDismiss?: () => void;
};

export type AlertContextProps = {
  alert$: BehaviorSubject<AlertProps | undefined>;
};

export const AlertContext = createContext<AlertContextProps>(null as any);

export const useAlert = () => {
  const { alert$ } = useContext(AlertContext);
  const setAlertProps = useBehaviorSubjectUpdater(alert$);
  return useCallback(
    async (params: {
      title: string;
      message: string | ReactNode;
      buttonText?: string | null;
      cancelButtonText?: string;
      contentContainerStyle?: StyleProp<ViewStyle>;
      disabled$?: BehaviorSubject<boolean | undefined>;
      loading$?: BehaviorSubject<boolean | undefined>;
      onConfirm?: () => Promise<boolean | undefined>;
    }) => {
      await new Promise<void>(resolve => {
        const buttons: AlertButtonProps[] = [];
        if (params.buttonText !== null) {
          buttons.push({
            text: params.buttonText ?? '确定',
            type: 'confirm',
            disabled$: params.disabled$,
            loading$: params.loading$,
            onPress: async () => {
              if (params.onConfirm != null) {
                const result = await params.onConfirm();
                if (result === false) return;
              }
              setAlertProps(undefined);
              resolve();
            },
          });
        }
        if (params.cancelButtonText != null) {
          buttons.unshift({
            text: params.cancelButtonText,
            type: 'cancel',
            onPress: () => {
              setAlertProps(undefined);
              resolve();
            },
          });
        }
        setAlertProps({
          title: params.title,
          message: params.message,
          buttons,
          contentContainerStyle: params.contentContainerStyle,
          onDismiss: () => {
            setAlertProps(undefined);
            resolve();
          },
        });
      });
    },
    [setAlertProps],
  );
};

export const useConfirmationAlert = () => {
  const { alert$ } = useContext(AlertContext);
  const setAlertProps = useBehaviorSubjectUpdater(alert$);

  return useCallback(
    async (params: {
      title: string;
      message: string | ReactNode;
      confirmButtonText?: string;
      cancelButtonText?: string;
      isConfirmButtonDestructive?: boolean;
      isCancelButtonDestructive?: boolean;
    }) => {
      return await new Promise<boolean>(resolve => {
        setAlertProps({
          title: params.title,
          message: params.message,
          buttons: [
            {
              text: params.cancelButtonText ?? '否',
              type: 'cancel',
              destructive: params.isCancelButtonDestructive,
              onPress: () => {
                setAlertProps(undefined);
                resolve(false);
              },
            },
            {
              text: params.confirmButtonText ?? '是',
              type: 'confirm',
              destructive: params.isConfirmButtonDestructive,
              onPress: () => {
                setAlertProps(undefined);
                resolve(true);
              },
            },
          ],
          onDismiss: () => {
            setAlertProps(undefined);
            resolve(false);
          },
        });
      });
    },
    [setAlertProps],
  );
};

export const AlertContextProvider = memo(
  ({ children }: { children?: ReactNode }) => {
    const { rpx } = useResponsiveContext();
    const alert$ = useCreation(
      () => new BehaviorSubject<AlertProps | undefined>(undefined),
      [],
    );
    const [alertProps] = useBehaviorSubject(alert$);
    const value = useMemo(() => ({ alert$ }), [alert$]);
    const theme = usePresentationTheme();
    return (
      <AlertContext.Provider value={value}>
        {children}
        <AppModal
          visible={alertProps != null}
          onDismiss={alertProps?.onDismiss}
          containerStyle={{ backgroundColor: theme.alert.backdropColor }}
          contentContainerStyle={[
            {
              maxWidth: rpx(750),
              backgroundColor: theme.alert.bgColor,
            },
            alertProps?.contentContainerStyle,
          ]}
        >
          {alertProps != null && <AlertContent {...alertProps} />}
        </AppModal>
      </AlertContext.Provider>
    );
  },
);

export const AlertContainer = memo(
  ({
    style,
    children,
  }: {
    style?: StyleProp<ViewStyle>;
    children?: ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      alert: { bgColor },
    } = usePresentationTheme();
    return (
      <View
        css={`
          align-items: center;
          padding: ${rpx(56)}px ${rpx(56)}px;
          background-color: ${bgColor};
        `}
        style={style}
      >
        {children}
      </View>
    );
  },
);

const AlertContent = memo(
  ({
    title,
    message,
    buttons,
    style,
    titleStyle,
    messageStyle,
  }: AlertProps) => {
    const { rpx } = useResponsiveContext();
    const {
      alert: { titleTextColor, messageTextColor },
    } = usePresentationTheme();
    return (
      <AlertContainer style={style}>
        {title ? (
          <Typography.Label
            medium
            size={rpx(36)}
            lineHeight={rpx(48)}
            color={titleTextColor}
            style={titleStyle}
          >
            {title}
          </Typography.Label>
        ) : null}
        <View
          css={`
            align-self: stretch;
          `}
        >
          <MaybeText
            text={message}
            css={`
              margin-top: ${rpx(64)}px;
              margin-bottom: ${rpx(64)}px;
              color: ${messageTextColor};
              font-family: ${FontFamily.NotoSans.Regular};
              font-weight: 400;
              font-size: ${rpx(30)}px;
              line-height: ${rpx(48)}px;
            `}
            style={messageStyle}
          />
        </View>
        <AlertButtonContainer buttons={buttons} />
      </AlertContainer>
    );
  },
);

export const AlertButtonContainer = memo(
  ({
    buttons,
    style,
  }: {
    buttons: AlertButtonProps[];
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <HStack
        justifyContent="center"
        css={`
          width: 100%;
        `}
        style={style}
      >
        {buttons.map(({ style: buttonStyle, ...buttonProps }, i) => (
          <AlertButton
            key={i}
            {...buttonProps}
            style={[buttonStyle, { marginLeft: rpx(16), marginRight: rpx(16) }]}
          />
        ))}
      </HStack>
    );
  },
);

export const AlertButton = memo(
  ({ text, type, destructive, style, onPress, ...props }: AlertButtonProps) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const {
      borderColor,
      textColor = theme.alert.defaultButtonTextColor,
      bgColor = 'transparent',
      activityIndicatorColor,
    } = useMemo<ButtonThemeProps>(() => {
      if (destructive) return theme.alert.buttons.destructive;
      return theme.alert.buttons[type ?? 'default'];
    }, [destructive, theme.alert.buttons, type]);

    const disabled$ = useCreation(
      () =>
        props.disabled$ ?? new BehaviorSubject<boolean | undefined>(undefined),
      [props.disabled$],
    );
    const loading$ = useCreation(
      () =>
        props.loading$ ?? new BehaviorSubject<boolean | undefined>(undefined),
      [props.loading$],
    );
    const [disabled] = useBehaviorSubject(disabled$);
    const [loading] = useBehaviorSubject(loading$);

    return (
      <TouchableOpacity
        css={`
          background-color: ${Array.isArray(bgColor) ? 'transparent' : bgColor};
          border-width: ${borderColor !== null ? 1 : 0}px;
          border-color: ${borderColor ?? 'transparent'};
          height: ${rpx(80)}px;
          border-radius: ${rpx(80) / 2}px;
          overflow: hidden;
          padding: 0 ${rpx(40)}px;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          opacity: ${disabled ? 0.35 : 1};
        `}
        style={style}
        disabled={disabled}
        onPress={onPress}
      >
        {Array.isArray(bgColor) && (
          <LinearGradient
            colors={bgColor}
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 0.5 }}
            style={StyleSheet.absoluteFill}
          />
        )}
        {loading && (
          <ActivityIndicator
            size={rpx(32)}
            color={activityIndicatorColor ?? textColor}
            css={`
              margin-right: ${rpx(8)}px;
            `}
          />
        )}
        <Typography.Label
          regular
          size={rpx(28)}
          color={textColor}
          numberOfLines={1}
          css={`
            text-align: center;
          `}
        >
          {text}
        </Typography.Label>
      </TouchableOpacity>
    );
  },
);
